import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';

import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';
import Tooltip from 'shopper/components/Tooltip';

import { sendEvent } from 'lib/gtag';
import { formatAsBRLWithSignal } from 'lib/number';

import Section from '../Section';
import PriceRanges from './PriceRanges';

const PRICE_RANGES = [
  {
    TITLE: 'Oba, o preço está incrível! 🤑',
    DESCRIPTION: ({ offerPrice }) => (
      <>
        De acordo com as ofertas recentes do Promobit consideramos o valor atual
        de <b>{formatAsBRLWithSignal(offerPrice)}</b> uma excelente oportunidade
        de compra.
      </>
    ),
  },
  {
    TITLE: 'Oba, o preço está bom! 😄',
    DESCRIPTION: ({ offerPrice }) => (
      <>
        De acordo com as ofertas recentes do Promobit consideramos o valor atual
        de <b>{formatAsBRLWithSignal(offerPrice)}</b> uma boa oportunidade de
        compra.
      </>
    ),
  },
  {
    TITLE: 'Poxa, o preço pode melhorar. 🙁',
    DESCRIPTION: ({ offerPrice }) => (
      <>
        De acordo com as ofertas recentes do Promobit o valor atual de{' '}
        <b>{formatAsBRLWithSignal(offerPrice)}</b> ainda pode melhorar para ser
        uma boa compra.
      </>
    ),
  },
];

const PriceHistory = ({
  gaEventCategory,
  offerPrice,
  offerReferencePrices: { zonePrice: range, minPrice, maxPrice },
}) => {
  const { TITLE, DESCRIPTION: Description } = PRICE_RANGES[range - 1];

  useEffect(() => {
    sendEvent({
      action: 'price_reference_appear',
      category: gaEventCategory,
    });
  }, []);

  return (
    <Section className="lg:mt-10">
      <div className="flex items-center gap-x-2">
        <Heading as="h2" size="size4">
          Histórico de preço
        </Heading>
        <Tooltip.Root>
          <Tooltip.Trigger>
            <Icon
              className="text-rd-primary-700 dark:text-rd-primary-700"
              name="info-sign-outline"
            />
          </Tooltip.Trigger>
          <Tooltip.Box className="w-max max-w-xs" size="size2">
            A Referência de Preço é calculada através de todas as ofertas
            aprovadas no Promobit para um determinado produto com pelo menos 5
            ofertas. Dessa forma é possível categorizar um preço entre
            &rdquo;bom&rdquo;, &rdquo;muito bom&rdquo; e &rdquo;incrível&rdquo;
          </Tooltip.Box>
        </Tooltip.Root>
      </div>
      <div className="flex flex-col items-center gap-x-6 lg:flex-row">
        <PriceRanges
          className="min-w-full lg:min-w-[396px]"
          maxPrice={maxPrice}
          minPrice={minPrice}
          offerPrice={offerPrice}
          range={range}
        />
        <div className="mt-6 space-y-3 lg:mr-60 lg:mt-0">
          <Text
            className="flex items-center text-neutral-low-300 dark:text-neutral-high-300"
            bold
          >
            {TITLE}
          </Text>
          <Text
            className="text-neutral-high-500 dark:text-neutral-high-300"
            size="size2"
          >
            <Description offerPrice={offerPrice} />
          </Text>
        </div>
      </div>
    </Section>
  );
};

PriceHistory.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerReferencePrices: PropTypes.shape({
    zonePrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
    minPrice: PropTypes.number.isRequired,
  }).isRequired,
};

export default memo(PriceHistory);
